import { Box, Stack, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { MilestoneDto } from 'tdc-web-backend/milestones/schemas';
import {
  mapIndustry,
  mapService,
  mapCurrency,
  EnumProjectMilestoneStatus,
} from 'tdc-web-backend/enums/enums';
import { useTranslation } from 'react-i18next';
import ControlledTextInput from '../../../../components/custom-inputs/ControlledTextInput';
import BaseInputLabel from '../../../../components/custom-inputs/BaseInputLabel';
import ControlledAutocompleteInput from '../../../../components/custom-inputs/ControlledAutocompleteInput';
import CustomInput from '../../../../components/custom-input/CustomInput';
import CustomInputAutocomplete from '../../../../components/custom-input/inputs/CustomInputAutocomplete';

const ScopeOfWorkForm = ({ disabled = false }: { disabled?: boolean }) => {
  const { getValues, setValue } = useFormContext();
  const paymentTermsDays = useWatch({ name: 'body.scope.paymentTermsDays' });
  const { t } = useTranslation();

  function handlePaymentTermsDaysChange(newPaymentTermsDays: number) {
    const milestones = getValues('body.milestones');

    milestones.forEach((milestone: MilestoneDto, i: number) => {
      if (
        milestone.status === EnumProjectMilestoneStatus.Pending ||
        milestone.status === EnumProjectMilestoneStatus.InProgress
      ) {
        const updatedMilestone = { ...milestone, paymentTermsDays: newPaymentTermsDays };
        setValue(`body.milestones.${i}`, updatedMilestone);
      }
    });
  }

  useEffect(() => {
    if (
      paymentTermsDays !== undefined &&
      paymentTermsDays !== null &&
      Number.isInteger(paymentTermsDays) &&
      paymentTermsDays >= 0
    ) {
      handlePaymentTermsDaysChange(paymentTermsDays);
    }
  }, [paymentTermsDays]);
  return (
    <Stack spacing={4} sx={{ mt: 4 }}>
      <Stack direction="row" spacing={4} justifyContent="space-between">
        <Stack width="50%" direction="row" spacing={4} justifyContent="space-between">
          <Stack width="45%" sx={{ maxWidth: '150px' }}>
            <BaseInputLabel text={t('contracts.projectCurrency')} />
            <CustomInput
              input={<CustomInputAutocomplete />}
              name="body.scope.currency"
              choices={mapCurrency}
              placeholder={t('contracts.select')}
              fullWidth
              sx={{ width: '100%', maxWidth: '280px', minHeight: 'none', mt: 2 }}
              disableClearable
              disabled={disabled}
            />
          </Stack>
          <Stack width="55%">
            <BaseInputLabel text={t('contracts.milestoneDueInDays')} />
            <CustomInput
              input={<CustomInput.Text />}
              name="body.scope.paymentTermsDays"
              type="number"
              placeholder="Type"
              rules={{
                validate: (value) =>
                  value === undefined ||
                  (Number.isInteger(value) && value >= 0) ||
                  t('errors.milestoneDueMustBeInteger'),
              }}
              fullWidth
              sx={{ width: '100%', maxWidth: '280px', minHeight: 'none', mt: 2 }}
              disabled={disabled}
            />
          </Stack>
        </Stack>

        <Stack width="40%" sx={{ maxWidth: '150px' }}>
          <BaseInputLabel required text={t('contracts.projectBudget')} />
          <Typography variant="body1" sx={{ mt: 2, fontWeight: 'bold' }}>
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: getValues('body.scope.currency'),
              notation: 'standard',
              maximumFractionDigits: 2,
            }).format(getValues('initialAmount'))}
          </Typography>
        </Stack>
      </Stack>

      <Stack spacing={2} direction="row">
        <Stack spacing={1} width="50%">
          <ControlledAutocompleteInput
            name="body.scope.services"
            label={t('contracts.servicesProvided')}
            translation={{ t, ns: 'enum' }}
            required
            multiple
            withChips
            choices={mapService}
            disabled={disabled}
          />
        </Stack>
        <Stack
          sx={{
            flex: 1,
            '.MuiBox-root': {
              flex: 1,
            },
          }}
          direction="row"
        >
          <ControlledAutocompleteInput
            name="body.scope.industry"
            label={t('contracts.clientIndustry')}
            required
            translation={{ t, ns: 'enum' }}
            choices={mapIndustry}
            fullWidth
            disabled={disabled}
            sx={{
              flex: 1,
            }}
          />
        </Stack>
      </Stack>

      <Box>
        <ControlledTextInput
          disabled={disabled}
          label={t('projects.projectDescription')}
          name="body.scope.description"
          multiline
          minRows={5}
          placeholder={t('common.type')}
          required
          variant="outlined"
        />
      </Box>
    </Stack>
  );
};

export default ScopeOfWorkForm;
